import Header from "components/header";
import Headroom from "react-headroom";
import MyBets from "./my-bets";
import TitleSection from "./title-section.tsx";
import ContestEntrySelector, {
  ContestEntryOption,
} from "components/leaderboard/contest-filter/contest-entry-selector.tsx";
import { useEffect, useState } from "react";
import { Contest, JoinedContest } from "interfaces/leaderboard/contest.ts";
import { getRequest } from "utils/httpClient.ts";
import {
  getContestsPath,
  getJoinedContestsPath,
  integratedGetContests,
  integratedGetJoinedContests,
} from "utils/backend-path-builders.ts";
import { toast } from "react-toastify";
import { UserGroupMembershipStatus } from "utils/userGroupMembershipStatus.ts";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import { getGroupState } from "state/slices/group-slice.ts";
import LoadingSpinner from "components/loading-spinner.tsx";
import useQueryParams from "utils/useQueryParams.ts";
import {
  getIntegratedAppState,
  IntegratedAppState,
} from "state/slices/integrated-app-slice.ts";
import {
  clearContestBanner,
  setContestBanner,
} from "state/slices/adContestSlice.ts";
import { getBetsState } from "state/slices/bets-slice.ts";
import { CONTEST_ENTRY_ID_QUERY_PARAM } from "utils/constants.ts";
import useWagerWire from "hooks/useWagerWire.ts";

const MyPicksPage = () => {
  const group = useAppSelector(getGroupState);
  const integratedAppState = useAppSelector(getIntegratedAppState);
  const { wagerWireClosed, setWagerWireClosed } = useWagerWire();

  const dispatch = useAppDispatch();

  const queryParams = useQueryParams();
  const contestEntryIdQueryPar: string | null = queryParams.get(
    CONTEST_ENTRY_ID_QUERY_PARAM
  );

  const [contestEntries, setContestEntries] = useState<ContestEntryOption[]>();
  const [selectedEntry, setSelectedEntry] = useState<
    ContestEntryOption | undefined
  >();
  const [groupContests, setGroupContests] = useState<Contest[]>();
  const userHasJoinedTheGroup =
    group.status === UserGroupMembershipStatus.APPROVED;

  const { loadingBets } = useAppSelector(getBetsState);

  const handleEntrySelection = (entry: ContestEntryOption | undefined) => {
    setSelectedEntry(entry);

    if (entry && groupContests) {
      const selectedContest = groupContests.find(
        (c) => c.id === entry.contestId
      );
      if (selectedContest?.settings?.ad_banner?.enabled) {
        dispatch(setContestBanner(selectedContest.settings.ad_banner));
      } else {
        dispatch(clearContestBanner());
      }
    } else {
      dispatch(clearContestBanner());
    }
  };

  useEffect(() => {
    setWagerWireClosed(false);

    if (integratedAppState.state === IntegratedAppState.UNDEFINED) {
      return;
    }

    const loadContests = async () => {
      try {
        const fetchContestsUrl =
          integratedAppState.state === IntegratedAppState.NOT_INTEGRATED
            ? getContestsPath(group.id)
            : integratedGetContests();

        const fetchJoinedContestsUrl =
          integratedAppState.state === IntegratedAppState.NOT_INTEGRATED
            ? getJoinedContestsPath(group.id)
            : integratedGetJoinedContests();

        const [contestsResponse, joinedContestsResponse] = await Promise.all([
          getRequest(fetchContestsUrl, { queryParams: { includeDeleted: 'true' } }) as Promise<Contest[]>,
          getRequest(fetchJoinedContestsUrl) as Promise<JoinedContest[]>,
        ]);

        // Set the groupContests state with the fetched contests (including deleted ones for name mapping)
        setGroupContests(contestsResponse);

        const entries: ContestEntryOption[] = joinedContestsResponse.flatMap(
          (jc: JoinedContest) =>
            jc.entries.map((entry) => ({
              contestEntryId: entry.id,
              contestEntryCreatedAt: entry.created_at,
              contestId: jc.contest_id,
              contestName:
                contestsResponse.find((c) => c.id === jc.contest_id)?.name ||
                "Unknown Contest",
            }))
        );

        if (contestEntryIdQueryPar) {
          const initialEntry = entries.find(
            (entry) => entry.contestEntryId === contestEntryIdQueryPar
          );
          handleEntrySelection(initialEntry);
        }

        setContestEntries(entries);
      } catch (error) {
        toast.error("Something went wrong");
      }
    };

    if (group.id && userHasJoinedTheGroup) {
      if (group.isContestGroup) {
        loadContests();
      } else {
        setContestEntries([]);
      }
    }
  }, [
    group.id,
    userHasJoinedTheGroup,
    group.isContestGroup,
    integratedAppState.state,
    dispatch,
    wagerWireClosed,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearContestBanner());
    };
  }, [dispatch]);

  if (userHasJoinedTheGroup && !contestEntries) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Headroom>
        <Header />
      </Headroom>
      <TitleSection />

      {contestEntries && contestEntries.length > 0 && (
        <div style={{ margin: 20 }}>
          <ContestEntrySelector
            contestEntries={contestEntries}
            setEntry={handleEntrySelection}
            disabled={loadingBets}
            selectedEntry={selectedEntry}
          />
        </div>
      )}

      <MyBets contestEntryId={selectedEntry?.contestEntryId} />
    </>
  );
};

export default MyPicksPage;
